import { SCENARIO_NUMBER_FIELDS, parseScenarioField, parseScheduleSlot } from './scenarioNumbers'
import { SCENARIO_DATE_FIELDS, toIsoDate } from './scenarioDates'

// The fields POST /properties/:id/flip/compute reads: reilm-be flipAnalysisFields.js's
// FLIP_ANALYSIS_WRITABLE_FIELDS minus title/created/modified/activeTemplate, which the formula core
// never reads (sending them would only refetch on a label change). Kept in sync by
// computePayload.test.js; see README.md ("Live computed values").
const STRING_FIELDS = ['scenarioType', 'sell_tax_applied']
const INTEREST_TYPES = new Set(['Non-Dutch', 'Dutch'])

export const COMPUTE_TOGGLE_FIELDS = new Set([
  'buy_commission',
  'insurance_policy',
  'buy_title_fee',
  'holding_hoa_fee',
  'holding_util',
  'selling_commission',
  'concessions_to_buyer',
  'br4_ltv',
  'br4_loan_cost',
  'sale_date',
  'financed_down_payment',
  'origination',
  'modified',
])

export const COMPUTE_FIELDS = new Set([
  ...SCENARIO_NUMBER_FIELDS,
  ...STRING_FIELDS,
  ...SCENARIO_DATE_FIELDS,
  'interest_type',
  'paySchedProrations',
  'toggles',
])

// A scenario as the screen holds it (a loaded document carries `_id` and the derived fields) ->
// exactly the body assertFlipAnalysisScenarioBody accepts. A key the validator would type-reject is
// dropped rather than sent, so one odd stored value cannot 400 every keystroke.
export function buildComputePayload(scenario) {
  const s = scenario ?? {}
  const body = {}
  for (const key of SCENARIO_NUMBER_FIELDS) {
    if (key in s) body[key] = parseScenarioField(key, s[key])
  }
  // ISO or null only: a non-ISO string passes the validator but reads as "no date" in the core.
  for (const key of SCENARIO_DATE_FIELDS) {
    if (key in s) body[key] = toIsoDate(s[key])
  }
  for (const key of STRING_FIELDS) {
    if (typeof s[key] === 'string' || s[key] === null) body[key] = s[key]
  }
  if (INTEREST_TYPES.has(s.interest_type)) body.interest_type = s.interest_type
  if (Array.isArray(s.paySchedProrations)) body.paySchedProrations = s.paySchedProrations.map(parseScheduleSlot)
  const toggles = {}
  for (const [key, value] of Object.entries(s.toggles ?? {})) {
    if (COMPUTE_TOGGLE_FIELDS.has(key) && (typeof value === 'string' || value === null)) toggles[key] = value
  }
  body.toggles = toggles
  return body
}

// A blank override shows the computed figure in grey. While a new compute is out, the last figure
// still holds if it was also computed from a blank override: only clearing a typed value makes it
// wrong (it would echo the typed number), so only that case hides it (letter 99, 8.1: no blink).
export function computedFallbackUsable(computeStale, computedInput, field) {
  if (!computeStale) return true
  return computedInput != null && computedInput[field] == null
}
